import { isNavbarItemAllowed } from 'features/navbar/isNavbarItemAllowed'
import { NAV_ITEMS_CONFIG } from 'features/navbar/navConfig'
import type { SelectedTab } from 'features/navbar/types'
import { useNavbarConfig } from 'features/navbar/useNavbarConfig'
import { useCanVisitSiteView } from 'features/overview'
import { useGeckoPermissions } from 'features/permissions'
import { isPermittedByGate } from 'features/permissions/utils/isPermittedByGate'
import { useCallback } from 'react'

/** Whether the sidebar would show the page for this user and site. */
export function useIsNavItemPermitted({
  siteId,
  orgId,
}: {
  siteId: string | undefined
  orgId: string | undefined
}): (tabKey: SelectedTab) => boolean {
  const { hasPermissions } = useGeckoPermissions()
  const canVisitSiteView = useCanVisitSiteView()
  const { allowedNavbarItems } = useNavbarConfig({
    orgId: orgId ?? '',
    siteId,
  })

  return useCallback(
    (tabKey: SelectedTab) => {
      const config = NAV_ITEMS_CONFIG[tabKey]
      if (!config) return false
      if (
        !isNavbarItemAllowed(tabKey, allowedNavbarItems, {
          requiresExplicitEnablement: config.requiresExplicitEnablement,
          enabledBy: config.enabledBy,
        })
      ) {
        return false
      }
      if (
        !isPermittedByGate(hasPermissions, {
          requires: config.permissions,
          except: config.except,
        })
      ) {
        return false
      }
      return (
        config.shouldShow?.({ siteID: siteId ?? '', canVisitSiteView }) ?? true
      )
    },
    [allowedNavbarItems, canVisitSiteView, hasPermissions, siteId]
  )
}
